import {
  ThreeToolsInstance,
  usePartDefinition,
  useProductStoreContext,
} from "@project/emerge";
import type { Sides } from "@project/emerge-types";
import * as MakerJS from "makerjs";
import { memo, useMemo } from "react";
import * as THREE from "three";
import {
  computeCutoutDxDy,
  getCachedSvgModel,
  transformCutoutModel,
} from "../geometry/cutoutPanelPlane";
import type { ExtendedCutoutDefinition } from "../interfaces/CutoutProperties";
import type { PanelColor } from "../interfaces/PanelColor";
import type { PanelDefinition } from "../interfaces/PanelProperties";
import type { TrayPanelDefinition } from "../interfaces/TrayPanelProperties";
import { PanelMaterial } from "../materials/PanelMaterial";
import { RectPropertiesBySide } from "../structure/RectProperties";

/**
 * Filtert doppelte Cutouts aus der Liste der Cutouts.
 * Ein Cutout gilt als Duplikat, wenn Position und Form identisch sind.
 * @param cutouts Liste der Cutouts
 * @returns Gefilterte Liste ohne Duplikate
 */
export function filterDuplicateCutouts(cutouts: ExtendedCutoutDefinition[]): {
  uniqueCutouts: ExtendedCutoutDefinition[];
  duplicates: Array<{ index: number; duplicateOfIndex: number }>;
} {
  if (!cutouts || cutouts.length === 0)
    return { uniqueCutouts: [], duplicates: [] };

  const uniqueCutouts: ExtendedCutoutDefinition[] = [];
  const duplicates: Array<{ index: number; duplicateOfIndex: number }> = [];
  const seenCutouts = new Map<string, number>();

  for (const [index, cutout] of cutouts.entries()) {
    // Erstelle einen eindeutigen Schlüssel basierend auf Position und Shape
    // Für Position verwenden wir die x,y,z Koordinaten, für Shape den String oder eine Hash-Repräsentation des IModel
    const positionKey = `${cutout.position.x},${cutout.position.y},${cutout.position.z}`;
    const shapeKey =
      typeof cutout.path === "string"
        ? cutout.path
        : JSON.stringify(cutout.path);
    const uniqueKey = `${positionKey}|${shapeKey}`;

    // Wenn wir diesen Schlüssel noch nicht gesehen haben, füge das Cutout zur Liste hinzu
    const existingIndex = seenCutouts.get(uniqueKey);
    if (existingIndex !== undefined) {
      // Speichere Duplikat-Information (aktueller Index und Index des Originals)
      duplicates.push({
        index,
        duplicateOfIndex: existingIndex,
      });
    } else {
      seenCutouts.set(uniqueKey, index);
      uniqueCutouts.push(cutout);
    }
  }

  return { uniqueCutouts, duplicates };
}

/**
 * Verarbeitung von SVG-Pfaden in Cutouts:
 * - Wenn ein SVG-String mehrere separate Pfade enthält (getrennt durch 'Z'),
 *   wird jeder Pfad als eigenständige Geometrie behandelt.
 * - Jede Geometrie wird als Loch im Panel-Rechteck hinzugefügt und dann extrudiert.
 * - Für Debugging werden die Pfade analysiert und visualisiert.
 */

/** Convert cutout chains to Three.js hole shapes with correct winding */
function convertChainsToHoles(cutoutModel: MakerJS.IModel): THREE.Shape[] {
  const holes: THREE.Shape[] = [];
  const chains = MakerJS.model.findChains(cutoutModel) as MakerJS.IChain[];

  for (const chain of chains) {
    const holeShape = ThreeToolsInstance.Chain2Shape(chain);
    const points = holeShape.getPoints();

    // Ensure hole has reverse winding (CW) relative to panel (CCW)
    if (THREE.ShapeUtils.isClockWise(points)) {
      holes.push(holeShape);
    } else {
      points.reverse();
      holes.push(new THREE.Shape(points));
    }
  }
  return holes;
}

/** Create extruded geometry from a shape with proper UV mapping */
function createExtrudedGeometry(
  panelShape: THREE.Shape,
  size: THREE.Vector2,
  strength: number,
): THREE.ExtrudeGeometry {
  const extrudeSettings = {
    depth: strength,
    bevelEnabled: false,
    steps: 1,
    curveSegments: 12,
  };

  const geometry = new THREE.ExtrudeGeometry(panelShape, extrudeSettings);
  geometry.translate(-size.x / 2, -size.y / 2, -strength / 2);

  // Manually set UV coordinates based on actual panel size
  // This ensures consistent texture mapping regardless of cutouts
  // The geometry is translated so it's centered: x from -size.x/2 to +size.x/2, y from -size.y/2 to +size.y/2
  const positions = geometry.attributes.position;
  if (!positions) {
    return geometry;
  }

  const uvs = new Float32Array(positions.count * 2);

  // Get the existing UV attribute to preserve face information
  const existingUVs = geometry.attributes.uv;

  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);

    // Determine which face this vertex belongs to by checking which coordinate is at the boundary
    const tolerance = 0.01;
    const atTopZ = Math.abs(z - strength / 2) < tolerance;
    const atBottomZ = Math.abs(z + strength / 2) < tolerance;

    if (atTopZ || atBottomZ) {
      // Top or bottom face (main panel surface) - map x,y directly to UV
      // This is the most important face for texture mapping
      uvs[i * 2] = (x + size.x / 2) / size.x;
      uvs[i * 2 + 1] = (y + size.y / 2) / size.y;
    } else {
      // Side face - use the existing UV mapping but scale it to panel dimensions
      // For side faces, we preserve the relative position but ensure it scales with panel size
      if (existingUVs) {
        // Use existing UV as base but adjust for panel size
        const baseU = existingUVs.getX(i);
        const baseV = existingUVs.getY(i);
        // Scale based on which dimension is being used
        // This is approximate but keeps side faces reasonable
        uvs[i * 2] = baseU;
        uvs[i * 2 + 1] = baseV;
      } else {
        // Fallback: map based on position
        uvs[i * 2] = (x + size.x / 2) / size.x;
        uvs[i * 2 + 1] = (z + strength / 2) / strength;
      }
    }
  }

  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));

  return geometry;
}

/** Transformed cutout with precomputed model and internal flag */
interface TransformedCutout {
  cutout: ExtendedCutoutDefinition;
  model: MakerJS.IModel;
  isInternal: boolean;
  hash: string;
  dx: number;
  dy: number;
}

/** Transform and classify a cutout */
function transformAndClassifyCutout(
  cutout: ExtendedCutoutDefinition,
  size: THREE.Vector2,
  position: THREE.Vector3,
  rectProps: (typeof RectPropertiesBySide)[Sides],
): TransformedCutout | null {
  if (!cutout.path || typeof cutout.path !== "string") return null;

  const { dx, dy } = computeCutoutDxDy(
    {
      x: cutout.position.x,
      y: cutout.position.y,
      z: cutout.position.z,
    },
    size,
    position,
    rectProps,
  );

  let cachedModel = getCachedSvgModel(cutout.path);
  const inv = (cutout as { invert?: { x?: boolean; y?: boolean } }).invert;
  if (inv?.x) {
    cachedModel = MakerJS.model.mirror(cachedModel, true, false);
  }
  if (inv?.y) {
    cachedModel = MakerJS.model.mirror(cachedModel, false, true);
  }
  const model = transformCutoutModel(
    cachedModel,
    dx,
    dy,
    cutout.orientation || 0,
  );

  // Check if fully inside panel bounds
  const measure = MakerJS.measure.modelExtents(model);
  const epsilon = 0.001;
  const isInternal =
    measure?.low &&
    measure?.high &&
    (measure.low[0] || 0) >= epsilon &&
    (measure.high[0] || 0) <= size.x - epsilon &&
    (measure.low[1] || 0) >= epsilon &&
    (measure.high[1] || 0) <= size.y - epsilon;

  const hash = `${dx},${dy},${cutout.orientation || 0},${cutout.path},${inv?.x ? "x" : ""}${inv?.y ? "y" : ""}`;

  return { cutout, model, isInternal: !!isInternal, hash, dx, dy };
}

// Cache for boundary shapes (outer panel shape after edge cutouts)
const boundaryShapeCache = new Map<string, THREE.Shape>();

// Cache for hole shapes by SVG path + orientation (position-independent)
const holeShapeCache = new Map<string, THREE.Shape[]>();

/** Generate cache key for boundary (panel size + edge cutouts) */
function generateBoundaryKey(
  size: THREE.Vector2,
  edgeCutouts: TransformedCutout[],
): string {
  const sizeKey = `${size.x},${size.y}`;
  const cutoutsKey = edgeCutouts.map((c) => c.hash).join("|");
  return `${sizeKey}::${cutoutsKey}`;
}

/** Get or compute hole shapes for an SVG path at origin (cached by path+orientation+invert) */
function getCachedHoleShapes(
  svgPath: string,
  orientation: number,
  invert?: { x?: boolean; y?: boolean },
): THREE.Shape[] {
  const cacheKey = `${svgPath}|${orientation}|${invert?.x ? "x" : ""}|${invert?.y ? "y" : ""}`;
  let shapes = holeShapeCache.get(cacheKey);

  if (!shapes) {
    // Compute shape at origin (legacy invert before SVG→panel mirror/rotate)
    let cachedModel = getCachedSvgModel(svgPath);
    if (invert?.x) {
      cachedModel = MakerJS.model.mirror(cachedModel, true, false);
    }
    if (invert?.y) {
      cachedModel = MakerJS.model.mirror(cachedModel, false, true);
    }
    const model = MakerJS.model.rotate(
      MakerJS.model.scale(
        MakerJS.model.mirror(cachedModel, false, true),
        1,
        true,
      ),
      orientation,
      [0, 0],
    );
    shapes = convertChainsToHoles(model);
    holeShapeCache.set(cacheKey, shapes);
  }

  return shapes;
}

/** Translate a shape by dx, dy */
function translateShape(
  shape: THREE.Shape,
  dx: number,
  dy: number,
): THREE.Shape {
  const points = shape.getPoints();
  const translatedPoints = points.map(
    (p) => new THREE.Vector2(p.x + dx, p.y + dy),
  );
  return new THREE.Shape(translatedPoints);
}

/** Compute boundary shape from edge cutouts (expensive - boolean ops) */
function computeBoundaryShape(
  size: THREE.Vector2,
  edgeCutouts: TransformedCutout[],
): THREE.Shape {
  let boundaryModel: MakerJS.IModel = new MakerJS.models.Rectangle(
    size.x,
    size.y,
  );

  for (const { model } of edgeCutouts) {
    try {
      boundaryModel = MakerJS.model.combineSubtraction(boundaryModel, model);
    } catch (error) {
      console.warn("Fehler beim Verarbeiten eines Rand-Cutouts:", error);
    }
  }

  const shape = ThreeToolsInstance.MakerJSModel2Shape(boundaryModel);

  // Ensure winding order is CCW
  if (THREE.ShapeUtils.isClockWise(shape.getPoints())) {
    const points = shape.getPoints().reverse();
    shape.curves = new THREE.Shape(points).curves;
  }

  return shape;
}

/** Compute internal holes from internal cutouts (uses shape cache + translation) */
function computeInternalHoles(
  internalCutouts: TransformedCutout[],
): THREE.Shape[] {
  const holes: THREE.Shape[] = [];
  for (const { cutout, dx, dy } of internalCutouts) {
    try {
      if (!cutout.path || typeof cutout.path !== "string") continue;
      const cachedShapes = getCachedHoleShapes(
        cutout.path,
        cutout.orientation || 0,
        (cutout as { invert?: { x?: boolean; y?: boolean } }).invert,
      );
      // Translate cached shapes to actual position
      for (const shape of cachedShapes) {
        holes.push(translateShape(shape, dx, dy));
      }
    } catch (error) {
      console.warn("Fehler beim Verarbeiten eines internen Cutouts:", error);
    }
  }
  return holes;
}

/**
 * Creates the panel geometry with cutouts using Three.js holes.
 * Caches boundary shape and internal holes separately for performance.
 */
export function createPanelGeometryWithHoles(
  size: THREE.Vector2,
  position: THREE.Vector3,
  side: Sides,
  cutouts: ExtendedCutoutDefinition[],
  strength: number,
): THREE.BufferGeometry {
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return new THREE.BoxGeometry(size.x, size.y, strength);
  }

  try {
    // Transform and classify all cutouts
    const transformed: TransformedCutout[] = [];
    for (const cutout of cutouts) {
      const result = transformAndClassifyCutout(
        cutout,
        size,
        position,
        rectProps,
      );
      if (result) transformed.push(result);
    }

    // Separate edge-touching and internal cutouts
    const edgeCutouts = transformed.filter((c) => !c.isInternal);
    const internalCutouts = transformed.filter((c) => c.isInternal);

    // Get or compute boundary shape (cached by edge cutouts)
    const boundaryKey = generateBoundaryKey(size, edgeCutouts);
    let boundaryShape = boundaryShapeCache.get(boundaryKey);
    if (!boundaryShape) {
      boundaryShape = computeBoundaryShape(size, edgeCutouts);
      boundaryShapeCache.set(boundaryKey, boundaryShape);
    }

    // Compute internal holes (shape computation is cached, only translation happens here)
    const internalHoles = computeInternalHoles(internalCutouts);

    // Clone boundary shape and add holes
    const panelShape = boundaryShape.clone();
    panelShape.holes.push(...internalHoles);

    return createExtrudedGeometry(panelShape, size, strength);
  } catch (error) {
    console.warn("Fehler bei der Extrusion:", error);
    return new THREE.BoxGeometry(size.x, size.y, strength);
  }
}

export const PanelRenderer = memo(({ id }: { id: string }) => {
  // Verwende usePartDefinition nur für gemeinsame Properties
  // color und material werden direkt aus fullPartDef gelesen
  const partDef = usePartDefinition<
    PanelDefinition &
      TrayPanelDefinition & {
        strength: number;
        side: Sides;
        orientation?: number;
      }
  >({
    id,
    select: [
      "size",
      "position",
      "rotation",
      "cutouts",
      "strength",
      "side",
      "orientation",
    ] as const,
  });

  // Filtere doppelte Cutouts - muss vor early return sein
  const { uniqueCutouts } = useMemo(
    () => filterDuplicateCutouts(partDef?.cutouts ?? []),
    [partDef?.cutouts],
  );

  // Extrahiere color und strength direkt aus dem Store - sollten immer gesetzt sein
  const useProductStore = useProductStoreContext();
  const fullPartDef = useProductStore((state) =>
    state.product?.partDefinitions.get(id),
  );

  // Color: Immer gesetzt (Fallback zu Default in BuildStep)
  // Fallback nur für Edge-Cases - eigentlich sollte color immer gesetzt sein
  const fallbackColor = { color: "#808080", metalic: undefined } as PanelColor;
  const color: PanelColor =
    fullPartDef && "color" in fullPartDef && fullPartDef.color
      ? (fullPartDef.color as PanelColor)
      : fallbackColor;

  // Strength: Immer gesetzt in fullPartDef (wird im BuildStep gesetzt)
  const strength =
    fullPartDef &&
    "strength" in fullPartDef &&
    typeof fullPartDef.strength === "number"
      ? fullPartDef.strength
      : 5;

  // Panel-Geometrie mit Ausschnitten erstellen - muss vor early return sein
  const panelGeometry = useMemo(() => {
    // Early return wenn Daten fehlen
    // Wichtig: partDef?.side === undefined prüfen, nicht !partDef?.side,
    // da side === 0 (z.B. Sides.LEFT) ein gültiger Wert ist
    if (!partDef?.size || !partDef?.position || partDef?.side === undefined) {
      return null;
    }

    // Casts needed: PanelDefinition & TrayPanelDefinition intersection creates 'never'
    // because they extend AbstractPartDefinition with different PartType literals
    const size = partDef.size as THREE.Vector2;
    const position = partDef.position as THREE.Vector3;
    const side = partDef.side;

    // Keine Cutouts vorhanden - einfach ein Rechteck extrudieren
    if (!uniqueCutouts || uniqueCutouts.length === 0) {
      return new THREE.BoxGeometry(size.x, size.y, strength);
    }

    return createPanelGeometryWithHoles(
      size,
      position,
      side,
      uniqueCutouts,
      strength,
    );
  }, [
    partDef?.size,
    partDef?.position,
    partDef?.side,
    strength,
    uniqueCutouts,
  ]);

  // Early return wenn Geometrie nicht erstellt werden konnte
  // (panelGeometry ist bereits null wenn partDef-Daten fehlen)
  if (!panelGeometry) return null;

  // Get orientation from part definition (for UnderlayPanels)
  // Orientation is in degrees, convert to radians for Three.js
  const orientationDeg = partDef?.orientation ?? 0;
  const orientationRad = (orientationDeg * Math.PI) / 180;

  // React Three Fiber props that Biome doesn't recognize - these are valid props
  const meshProps = {
    rotation: [Math.PI / 2, 0, orientationRad] as [number, number, number],
  };
  const primitiveProps = {
    object: panelGeometry,
    attach: "geometry",
  };
  const lineSegmentsProps = {
    rotation: [Math.PI / 2, 0, orientationRad] as [number, number, number],
  };
  const edgesGeometryProps = {
    args: [panelGeometry, 10] as [THREE.BufferGeometry, number],
  };

  // Get panel size for texture scaling
  const panelSize = partDef?.size as THREE.Vector2 | undefined;

  // Only apply leather texture to outer panels (from OuterPanelBuildStep)
  // Other panels like TrayPanels, RollerBoards, etc. should not have the texture
  const isOuterPanel = fullPartDef?.buildstepName === "OuterPanelBuildStep";

  return (
    <group>
      {/* Base rotation matches standard: rotate 90° around X so Z becomes Y */}
      {/* Additional orientation rotation from buildstep (for UnderlayPanels) */}
      <mesh {...meshProps}>
        <primitive {...primitiveProps} />
        <PanelMaterial
          color={color}
          panelSize={panelSize}
          textured={isOuterPanel}
        />
      </mesh>

      {/* Schwarze Kanten nur bei signifikanten Winkeln (> 10°) */}
      <lineSegments {...lineSegmentsProps}>
        <edgesGeometry {...edgesGeometryProps} />
        <lineBasicMaterial color="black" />
      </lineSegments>
    </group>
  );
});

PanelRenderer.displayName = "PanelRenderer";
